import { Box, BoxProps } from "./Box"
import { Button, ButtonProps } from "./Button"
import type { ElementType } from "react"
import classNames from "classnames"
import { Divider } from "./Divider"
import { useLayoutEffect, useRef, useCallback } from "react"
import {
	dropdownCaretClassName,
	dropdownClassName,
	dropdownMenuClassName,
	dropdownMenuSectionHeaderClassName,
	dropdownTogglerClassName,
	dropdownTopClassName,
} from "./dropdown.css"

const DefaultDropdownElement = "div"

export type DropdownProps<
	T extends ElementType = typeof DefaultDropdownElement,
> = BoxProps<T> & {
	alignRight?: boolean | "sm"
	noCaret?: boolean
}
export function Dropdown<
	T extends ElementType = typeof DefaultDropdownElement,
>({
	className,
	alignRight = false,
	children,
	noCaret,
	as,
	onMouseEnter,
	onPointerDown,
	onTouchStart,
	...otherProps
}: DropdownProps<T>) {
	const Element: ElementType = as || DefaultDropdownElement
	const dropdownRef = useRef<HTMLDivElement>(null)
	const scrollCleanupRef = useRef<(() => void) | null>(null)

	const resetMenuFixedStyles = useCallback((menu: HTMLElement) => {
		menu.style.position = ""
		menu.style.top = ""
		menu.style.bottom = ""
		menu.style.left = ""
		menu.style.right = ""
		menu.style.width = ""
		menu.style.zIndex = ""
	}, [])

	const updatePlacement = () => {
		const el = dropdownRef.current
		if (!el) return

		const menu = el.querySelector(
			"." + dropdownMenuClassName
		) as HTMLElement | null
		if (!menu) return

		// temporarily show the menu to calculate its dimensions
		const prevDisplay = menu.style.display
		const prevVisibility = menu.style.visibility

		menu.style.display = "block"
		menu.style.visibility = "hidden"

		const triggerRect = el.getBoundingClientRect()
		const menuHeight = menu.offsetHeight
		const menuWidth = menu.offsetWidth
		const viewportHeight = window.innerHeight

		// restore styles
		menu.style.display = prevDisplay
		menu.style.visibility = prevVisibility

		const spaceBelow = viewportHeight - triggerRect.bottom
		const spaceAbove = triggerRect.top
		const shouldPlaceTop = spaceBelow < menuHeight && spaceAbove > spaceBelow

		el.classList.toggle(dropdownTopClassName, shouldPlaceTop)

		scrollCleanupRef.current?.()
		scrollCleanupRef.current = null

		const inTable = !!el.closest("table")
		if (inTable && shouldPlaceTop) {
			const stickMenuToTrigger = (rect: DOMRect) => {
				const right =
					alignRight === true ? window.innerWidth - rect.right : undefined
				const left = alignRight === true ? undefined : rect.left
				menu.style.position = "fixed"
				menu.style.top = "auto"
				menu.style.bottom = `${window.innerHeight - rect.top}px`
				menu.style.left = left !== undefined ? `${left}px` : ""
				menu.style.right = right !== undefined ? `${right}px` : ""
				menu.style.width = `${menuWidth}px`
				menu.style.zIndex = "9999"
			}
			stickMenuToTrigger(triggerRect)
			let stickRafId = 0
			const onScroll = (e: Event) => {
				if (menu.contains(e.target as Node)) return
				if (el.matches(":hover") || menu.matches(":hover")) {
					if (stickRafId) return
					stickRafId = window.requestAnimationFrame(() => {
						stickRafId = 0
						stickMenuToTrigger(el.getBoundingClientRect())
					})
					return
				}
				if (stickRafId) {
					window.cancelAnimationFrame(stickRafId)
					stickRafId = 0
				}
				resetMenuFixedStyles(menu)
				el.classList.remove(dropdownTopClassName)
				window.removeEventListener("scroll", onScroll, { capture: true })
				scrollCleanupRef.current = null
			}
			window.addEventListener("scroll", onScroll, {
				passive: true,
				capture: true,
			})
			scrollCleanupRef.current = () => {
				if (stickRafId) {
					window.cancelAnimationFrame(stickRafId)
					stickRafId = 0
				}
				window.removeEventListener("scroll", onScroll, { capture: true })
			}
		} else {
			resetMenuFixedStyles(menu)
		}
	}

	useLayoutEffect(() => {
		const dropdownElement = dropdownRef.current

		return () => {
			if (!dropdownElement) return
			// clean up any active scroll listener
			scrollCleanupRef.current?.()
			scrollCleanupRef.current = null
			dropdownElement.classList.remove(dropdownTopClassName)
			// reset any fixed positioning applied for table stacking context fix
			const menu = dropdownElement.querySelector(
				"." + dropdownMenuClassName
			) as HTMLElement | null
			if (menu) resetMenuFixedStyles(menu)
		}
	}, [resetMenuFixedStyles])

	return (
		<Box
			ref={dropdownRef}
			as={Element}
			className={classNames(
				dropdownClassName,
				{
					"align-right": alignRight === true,
					"sm:align-right": alignRight === "sm",
				},
				className
			)}
			onMouseEnter={(e: React.MouseEvent<never>) => {
				updatePlacement()
				onMouseEnter?.(e)
			}}
			onPointerDown={(e: React.PointerEvent<never>) => {
				updatePlacement()
				onPointerDown?.(e)
			}}
			onTouchStart={(e: React.TouchEvent<never>) => {
				updatePlacement()
				onTouchStart?.(e)
			}}
			{...otherProps}
		>
			{!noCaret ? <span className={dropdownCaretClassName} /> : null}
			{children}
		</Box>
	)
}

function DropdownMenu({ className, ...props }: BoxProps<"ul">) {
	return (
		<Box
			as="ul"
			className={classNames(dropdownMenuClassName, className)}
			{...props}
		/>
	)
}

const DefaultDropdownMenuItemElement = "a"
function MenuItem<
	T extends ElementType = typeof DefaultDropdownMenuItemElement,
>({ className, color, as, ...props }: BoxProps<T>) {
	const Element: ElementType = as || DefaultDropdownMenuItemElement
	return (
		<Box as="li" color={color}>
			<Box as={Element} href="#" {...props} />
		</Box>
	)
}

function MenuItemDivider(props: React.ComponentProps<typeof Divider>) {
	return <Divider sm {...props} />
}

function MenuSectionHeader({
	className,
	children,
	as,
	...props
}: BoxProps<"li">) {
	return (
		<Box
			as="li"
			color="muted"
			className={classNames(dropdownMenuSectionHeaderClassName, className)}
			fontWeight="semibold"
			fontSize="sm"
			{...props}
		>
			{children}
		</Box>
	)
}

const DefaultDropdownTogglerElement = "a"

function DropdownToggler<
	T extends ElementType = typeof DefaultDropdownTogglerElement,
>({ className, as, ...props }: BoxProps<T>) {
	const Element: ElementType = as || DefaultDropdownTogglerElement
	return (
		<Box
			as={Element}
			className={classNames(dropdownTogglerClassName, className)}
			{...props}
		/>
	)
}

function DropdownTogglerButton({
	className,
	children,
	as,
	...props
}: ButtonProps<typeof Button>) {
	return (
		<Button
			className={classNames(dropdownTogglerClassName, className)}
			{...props}
		>
			{children}
		</Button>
	)
}

Dropdown.Toggle = DropdownToggler
Dropdown.ToggleButton = DropdownTogglerButton
Dropdown.Menu = DropdownMenu
Dropdown.MenuItem = MenuItem
Dropdown.MenuItemDivider = MenuItemDivider
Dropdown.MenuSectionHeader = MenuSectionHeader
